import React, { useEffect, useRef } from "react";
import { isDiwaliSeason } from "./diwaliSeason";
import "./diwali-theme.css";

const COLORS = ["#ffd166", "#ff4d6d", "#b48cff", "#65d9ff", "#fff3bd", "#ff9f43"];
const rand = (min, max) => min + Math.random() * (max - min);
const pick = (values) => values[Math.floor(Math.random() * values.length)];

export default function DiwaliSiteTheme() {
  const canvasRef = useRef(null);
  const active = isDiwaliSeason();

  useEffect(() => {
    if (!active) return undefined;
    document.body.classList.add("diwali-site");
    return () => document.body.classList.remove("diwali-site");
  }, [active]);

  useEffect(() => {
    if (!active) return undefined;
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!context) return undefined;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reducedMotion) return undefined;

    let width = 0;
    let height = 0;
    let frameId = 0;
    let nextLaunch = 0;
    let lastDraw = 0;
    let lastCursorSpark = 0;
    const rockets = [];
    const sparks = [];

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      // Cap the backing store on large / HiDPI displays; CSS scales the decorative canvas to the viewport.
      const scale = Math.min(1, 1440 / width, 900 / height);
      canvas.width = Math.round(width * scale);
      canvas.height = Math.round(height * scale);
      context.setTransform(scale, 0, 0, scale, 0, 0);
      context.clearRect(0, 0, width, height);
    };

    const addSpark = (spark) => {
      if (sparks.length < 1200) sparks.push(spark);
    };

    const launch = (x = rand(width * 0.12, width * 0.88)) => {
      const y = height + 4;
      const targetY = rand(height * .14, height * .33);
      const flightFrames = rand(43, 57);
      const gravity = .2;
      const vy = -(y - targetY + .5 * gravity * flightFrames * flightFrames) / flightFrames;
      rockets.push({ x, y, flightFrames, age: 0, vx: rand(-.65, .65), vy, color: pick(COLORS), style: pick(["peony", "ring", "willow", "crackle"]) });
    };
    const launchPair = () => {
      launch(rand(width * .16, width * .42));
      launch(rand(width * .58, width * .84));
    };

    const burst = (x, y, color, style) => {
      const count = style === "ring" ? 64 : style === "willow" ? 84 : 76;
      for (let index = 0; index < count; index += 1) {
        const angle = style === "ring" ? (index / count) * Math.PI * 2 : rand(0, Math.PI * 2);
        const speed = style === "willow" ? rand(1, 3.4) : rand(1.4, 4.5);
        addSpark({
          x, y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          life: 0,
          max: style === "willow" ? rand(58, 92) : rand(40, 64),
          color: style === "willow" ? pick(["#ffd166", "#fff0bb", "#ffb52e"]) : index % 5 === 0 ? pick(COLORS) : color,
          gravity: style === "willow" ? .038 : .052,
          drag: .982,
          size: rand(1.2, 2.3),
          crackle: style === "crackle",
        });
      }
      // A brilliant central flash makes each burst read clearly over light pages too.
      addSpark({ x, y, vx: 0, vy: 0, life: 0, max: 9, color: "#fffbe9", gravity: 0, drag: 1, size: 9, flash: true });
    };

    const draw = (now) => {
      // Keep the effect smooth at 30fps while leaving the rest of the page more headroom.
      if (now - lastDraw < 1000 / 30) {
        frameId = window.requestAnimationFrame(draw);
        return;
      }
      const step = lastDraw ? Math.min((now - lastDraw) / (1000 / 60), 3) : 1;
      lastDraw = now;
      context.globalCompositeOperation = "destination-out";
      context.fillStyle = "rgba(0, 0, 0, 0.19)";
      context.fillRect(0, 0, width, height);
      context.globalCompositeOperation = "lighter";

      if (!document.hidden && now >= nextLaunch) {
        launchPair();
        nextLaunch = now + rand(1150, 1750);
      }

      for (let index = rockets.length - 1; index >= 0; index -= 1) {
        const rocket = rockets[index];
        rocket.x += rocket.vx * step;
        rocket.y += rocket.vy * step;
        rocket.age += step;
        rocket.vy += .2 * step;
        addSpark({ x: rocket.x, y: rocket.y + 8, vx: rand(-.55, .55), vy: rand(1.2, 2.5), life: 0, max: rand(13, 21), color: pick([rocket.color, "#fff0bb", "#ffb52e"]), gravity: .025, drag: .94, size: rand(1.1, 1.9) });

        const trail = context.createLinearGradient(rocket.x, rocket.y + 34, rocket.x, rocket.y - 4);
        trail.addColorStop(0, "rgba(255,255,255,0)");
        trail.addColorStop(.56, rocket.color);
        trail.addColorStop(1, "#fffef5");
        context.globalAlpha = .9;
        context.fillStyle = trail;
        context.fillRect(rocket.x - 1.5, rocket.y - 3, 3, 38);
        context.beginPath();
        context.arc(rocket.x, rocket.y - 4, 2.6, 0, Math.PI * 2);
        context.fillStyle = "#fffef5";
        context.shadowColor = rocket.color;
        context.shadowBlur = 10;
        context.fill();
        context.shadowBlur = 0;
        context.globalAlpha = 1;

        if (rocket.age >= rocket.flightFrames) {
          burst(rocket.x, rocket.y, rocket.color, rocket.style);
          rockets.splice(index, 1);
        }
      }

      for (let index = sparks.length - 1; index >= 0; index -= 1) {
        const spark = sparks[index];
        spark.life += step;
        spark.vx *= Math.pow(spark.drag, step);
        spark.vy = spark.vy * Math.pow(spark.drag, step) + spark.gravity * step;
        spark.x += spark.vx * step;
        spark.y += spark.vy * step;
        if (spark.life >= spark.max) {
          if (spark.crackle && Math.random() < .16) {
            for (let child = 0; child < 2; child += 1) addSpark({ x: spark.x, y: spark.y, vx: rand(-1.8, 1.8), vy: rand(-1.8, 1.8), life: 0, max: 10, color: "#fff8dc", gravity: .035, drag: .91, size: 1.2 });
          }
          sparks.splice(index, 1);
          continue;
        }
        const fade = 1 - spark.life / spark.max;
        context.globalAlpha = spark.flash ? fade * .8 : fade;
        if (spark.flash) {
          const glow = context.createRadialGradient(spark.x, spark.y, 0, spark.x, spark.y, 38);
          glow.addColorStop(0, "rgba(255, 250, 225, .9)");
          glow.addColorStop(.18, "rgba(255, 222, 140, .55)");
          glow.addColorStop(1, "rgba(255, 222, 140, 0)");
          context.fillStyle = glow;
          context.fillRect(spark.x - 38, spark.y - 38, 76, 76);
        } else {
          context.fillStyle = spark.color;
          // Additive compositing keeps the particles bright without per-particle shadowBlur cost.
          context.shadowBlur = 0;
          context.beginPath();
          context.arc(spark.x, spark.y, spark.size * (.45 + fade * .55), 0, Math.PI * 2);
          context.fill();
          context.shadowBlur = 0;
        }
      }
      context.globalAlpha = 1;
      context.globalCompositeOperation = "source-over";
      frameId = window.requestAnimationFrame(draw);
    };

    resize();
    // Opening salvo: launch two rockets together when the site loads or refreshes.
    launchPair();
    nextLaunch = performance.now() + 1250;

    const onPointerMove = (event) => {
      const now = performance.now();
      if (now - lastCursorSpark < 38 || document.hidden) return;
      lastCursorSpark = now;
      for (let index = 0; index < 2; index += 1) {
        addSpark({
          x: event.clientX + rand(-3, 3),
          y: event.clientY + rand(-3, 3),
          vx: rand(-1.25, 1.25),
          vy: rand(-1.5, .8),
          life: 0,
          max: rand(18, 32),
          color: pick(["#ffd166", "#fff3bd", "#ff9f43", "#ff4d6d"]),
          gravity: .035,
          drag: .94,
          size: rand(1.1, 1.8),
        });
      }
    };
    const onPointerClick = (event) => {
      if (document.hidden) return;
      burst(event.clientX, event.clientY, pick(COLORS), pick(["peony", "ring", "crackle"]));
    };

    window.addEventListener("resize", resize);
    document.addEventListener("pointermove", onPointerMove, { passive: true });
    document.addEventListener("click", onPointerClick);
    const resume = () => {
      if (document.hidden) {
        window.cancelAnimationFrame(frameId);
        frameId = 0;
      } else if (!frameId) {
        lastDraw = 0;
        frameId = window.requestAnimationFrame(draw);
      }
    };
    document.addEventListener("visibilitychange", resume);
    if (!document.hidden) frameId = window.requestAnimationFrame(draw);
    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", resize);
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("click", onPointerClick);
      document.removeEventListener("visibilitychange", resume);
      context.clearRect(0, 0, width, height);
    };
  }, [active]);

  if (!active) return null;
  return (
    <div className="diwali-atmosphere" aria-hidden="true">
      <canvas ref={canvasRef} className="diwali-fireworks-canvas" />
      <span className="diwali-edge-lamp diwali-edge-lamp--left"><Lamp /></span>
      <span className="diwali-edge-lamp diwali-edge-lamp--right"><Lamp /></span>
      <span className="diwali-atmo-glint diwali-atmo-glint--one" />
      <span className="diwali-atmo-glint diwali-atmo-glint--two" />
      <span className="diwali-atmo-glint diwali-atmo-glint--three" />
    </div>
  );
}

function Lamp() {
  return (
    <svg viewBox="0 0 54 64">
      <path d="M6 36c4 22 38 22 42 0-7 8-35 8-42 0Z" fill="#743c28" stroke="#d9963d" strokeWidth="1.8" />
      <path d="M22 34c-5-9-1-18 5-25 7 9 8 18 3 25Z" fill="#f89b21" />
      <path d="M25 33c-2-6 0-12 2-16 4 7 4 12 2 16Z" fill="#fff0b4" />
      <ellipse cx="27" cy="37" rx="22" ry="4" fill="none" stroke="#f3d89e" strokeWidth="1" opacity=".8" />
    </svg>
  );
}
